
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { NetworkRuleDetail } from "./NetworkRuleDetail";
import { NetworkRulesFilters } from "./NetworkRulesFilters";
import { NetworkRulesTabs } from "./NetworkRulesTabs";

import { NetworkRulesLoadingTable } from "./NetworkRulesLoadingTable";
import { EmptyNetworkRuleState } from "./EmptyNetworkRuleState";
import { isBundleIdRule, type GroupedRule } from "./NetworkRulesUtils";
import { useNetworkRules, type NetworkRule } from "@/hooks/useNetworkRules";
import { useDeleteNetworkRule } from "@/hooks/useDeleteNetworkRule";
import { useToast } from "@/hooks/use-toast";
import { logNetworkRules } from "@/utils/networkRulesLogger";

interface NetworkRulesSectionProps {
  onAddClick?: () => void;
}

export const NetworkRulesSection = ({ onAddClick }: NetworkRulesSectionProps = {}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRule, setSelectedRule] = useState<NetworkRule | null>(null);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { data: networkRules = [], isLoading, error, meta } = useNetworkRules();
  const deleteRuleMutation = useDeleteNetworkRule();
  const { toast } = useToast();

  // Use the Convex loading state from meta instead of React Query loading state
  const isConvexLoading = meta?.isConvexLoading || false;

  // Temporary: Log all network rules to console
  useEffect(() => {
    if (!isConvexLoading && networkRules.length > 0) {
      logNetworkRules(networkRules);
    }
  }, [networkRules, isConvexLoading]);

  // Get current filter tab from URL, default to 'system-wide'
  const currentFilterTab = searchParams.get('filterTab') || 'system-wide';

  // Handle filter tab changes and update URL
  const handleFilterTabChange = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('filterTab', tab);
    setSearchParams(newParams);
  };

  // Handle URL parameters for state persistence
  useEffect(() => {
    if (networkRules.length > 0) {
      const ruleId = searchParams.get('rule');
      if (ruleId) {
        const rule = networkRules.find(r => r.id === ruleId);
        if (rule) {
          setSelectedRule(rule);
        } else {
          // Rule not found, clear the URL parameter
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('rule');
          newParams.delete('tab');
          setSearchParams(newParams);
        }
      }
    }
  }, [networkRules, searchParams, setSearchParams]);

  // Update URL when rule selection changes
  const handleRuleSelect = (rule: NetworkRule | null) => {
    setSelectedRule(rule);
    const newParams = new URLSearchParams(searchParams);
    
    if (rule) {
      newParams.set('rule', rule.id);
      // Keep existing tab parameter if it exists
    } else {
      newParams.delete('rule');
      newParams.delete('tab');
    }
    
    setSearchParams(newParams);
  };

  // Persist selected rule in localStorage
  useEffect(() => {
    const savedRuleId = localStorage.getItem('selectedNetworkRuleId');
    if (savedRuleId && networkRules.length > 0) {
      const rule = networkRules.find(r => r.id === savedRuleId);
      if (rule) {
        setSelectedRule(rule);
      }
    }
  }, [networkRules]);

  useEffect(() => {
    if (selectedRule) {
      localStorage.setItem('selectedNetworkRuleId', selectedRule.id);
    } else {
      localStorage.removeItem('selectedNetworkRuleId');
    }
  }, [selectedRule]);

  // Group rules by app (excluding Bundle ID rules from app grouping)
  const groupedRules = networkRules.reduce((acc: GroupedRule[], rule) => {
    if (rule.type === "General" && !isBundleIdRule(rule)) {
      // General rules without bundle ID get their own group
      const existingGeneral = acc.find(group => group.appName === "System Wide");
      if (existingGeneral) {
        existingGeneral.rules.push(rule);
      } else {
        acc.push({
          appName: "System Wide",
          bundleId: "system.wide",
          iconUrl: "",
          rules: [rule]
        });
      }
    } else if (rule.type === "App Specific" && !isBundleIdRule(rule)) {
      // For app-specific rules, group by parent app if available
      if (rule.parentApp) {
        const existingGroup = acc.find(group => group.bundleId === rule.parentApp!.bundleId);
        if (existingGroup) {
          existingGroup.rules.push(rule);
        } else {
          acc.push({
            appName: rule.parentApp.name || 'Unknown App',
            bundleId: rule.parentApp.bundleId || '',
            iconUrl: rule.parentApp.iconUrl || "",
            rules: [rule]
          });
        }
      } else if (rule.apps && rule.apps.length > 0) {
        // Fallback to legacy apps field
        rule.apps.forEach(app => {
          const existingGroup = acc.find(group => group.bundleId === app.bundleId);
          if (existingGroup) {
            existingGroup.rules.push(rule);
          } else {
            acc.push({
              appName: app.appName || 'Unknown App',
              bundleId: app.bundleId || '',
              iconUrl: app.iconUrl || "",
              rules: [rule]
            });
          }
        });
      } else if (rule.bundleId) {
        // Legacy format
        const existingGroup = acc.find(group => group.bundleId === rule.bundleId);
        if (existingGroup) {
          existingGroup.rules.push(rule);
        } else {
          acc.push({
            appName: rule.appName || "Unknown App",
            bundleId: rule.bundleId || '',
            iconUrl: "",
            rules: [rule]
          });
        }
      }
    }
    // Bundle ID rules are not grouped - they will be handled separately
    return acc;
  }, []);

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await deleteRuleMutation.mutateAsync(ruleId);
      
      toast({
        title: "Rule Deleted",
        description: "The network rule has been successfully deleted.",
      });
      
      // Navigate back to the list view and clear URL params
      handleRuleSelect(null);
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast({
        title: "Error",
        description: "Failed to delete rule. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Filter groups based on current tab with proper null checks
  const getFilteredGroups = (filterType: string) => {
    return groupedRules.filter(group => {
      // Safe string operations with null checks
      const safeToLower = (str?: string) => (str || '').toLowerCase();
      
      const matchesSearch = safeToLower(group.appName).includes(safeToLower(searchTerm)) ||
                           safeToLower(group.bundleId).includes(safeToLower(searchTerm)) ||
                           group.rules.some(rule => safeToLower(rule.name).includes(safeToLower(searchTerm)));
      
      let matchesType = true;
      if (filterType === "system-wide") {
        matchesType = group.bundleId === "system.wide";
      } else if (filterType === "app-specific") {
        matchesType = group.bundleId !== "system.wide";
      }
      
      // Include all rules (no function filtering needed)
      const filteredRules = group.rules;
      
      // Only include the group if it has matching rules after filtering
      group.rules = filteredRules;
      
      return matchesSearch && matchesType && filteredRules.length > 0;
    });
  };

  const toggleAppExpansion = (bundleId: string) => {
    const newExpanded = new Set(expandedApps);
    if (newExpanded.has(bundleId)) {
      newExpanded.delete(bundleId);
    } else {
      newExpanded.add(bundleId);
    }
    setExpandedApps(newExpanded);
  };

  const handleRuleClick = (rule: NetworkRule) => {
    console.log('Rule clicked:', rule);
    handleRuleSelect(rule);
  };

  if (error) {
    return (
      <div className="w-full max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error loading network rules. Please try again.</div>
        </div>
      </div>
    );
  }

  // If a rule is selected, show the detail view
  if (selectedRule) {
    // Update selected rule with latest data from the query
    const updatedRule = networkRules.find(r => r.id === selectedRule.id);
    const currentRule = updatedRule || selectedRule;
    
    return (
      <NetworkRuleDetail 
        rule={currentRule} 
        onBack={() => handleRuleSelect(null)}
        onDelete={handleDeleteRule}
      />
    );
  }

  // Show loading state while Convex data is being fetched
  if (isConvexLoading) {
    return (
      <div className="w-full max-w-full mx-auto px-6 overflow-x-hidden">
        <div className="space-y-6">
        <NetworkRulesFilters searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAddClick={onAddClick} />
          <NetworkRulesLoadingTable />
        </div>
      </div>
    );
  }

  // Show empty state only after loading is complete and no rules exist
  if (networkRules.length === 0) {
    return (
      <div className="w-full max-w-full mx-auto px-6 overflow-x-hidden">
        <EmptyNetworkRuleState />
      </div>
    );
  }

  // Calculate counts for tab labels
  const systemWideCount = groupedRules.filter(g => g.bundleId === "system.wide").reduce((total, group) => total + group.rules.length, 0);
  const appSpecificCount = groupedRules.filter(g => g.bundleId !== "system.wide").reduce((total, group) => total + group.rules.length, 0);

  return (
    <div className="w-full max-w-full mx-auto px-6 overflow-x-hidden">
      <div className="space-y-6">
        <NetworkRulesFilters searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAddClick={onAddClick} />
        
        <NetworkRulesTabs
          currentFilterTab={currentFilterTab}
          handleFilterTabChange={handleFilterTabChange}
          systemWideCount={systemWideCount}
          appSpecificCount={appSpecificCount}
          getFilteredGroups={getFilteredGroups}
          expandedApps={expandedApps}
          toggleAppExpansion={toggleAppExpansion}
          onRuleClick={handleRuleClick}
        />
      </div>
    </div>
  );
};
